import { GroupMode, type PresetView } from '@bringg/types';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { PresetViewBase } from './preset-view-base';
import { TasksTableConfig } from './types';

const FALLBACK_COLUMNS: TasksTableConfig['columns'] = [
	{
		id: 'id',
		title: 'DISPATCH_LIST.ID',
		active: true,
		position: 0,
		title_translate_key: 'DISPATCH_LIST.ID'
	},
	{
		id: 's',
		title: 'DISPATCH_LIST.STATUS',
		active: true,
		position: 1,
		title_translate_key: 'DISPATCH_LIST.STATUS'
	},
	{
		id: 'order',
		title: 'DISPATCH_LIST.ORDER',
		active: true,
		position: 2,
		title_translate_key: 'DISPATCH_LIST.ORDER'
	},
	{
		id: 'assignedTo',
		title: 'DISPATCH_LIST.ASSIGNED_TO',
		active: true,
		position: 3,
		title_translate_key: 'DISPATCH_LIST.ASSIGNED_TO'
	}
];

export class TasksPresetView extends PresetViewBase {
	_groupMode: TasksTableConfig['groupMode'] = null;
	_sortOptions: TasksTableConfig['sortOptions'] = null;
	_columns: TasksTableConfig['columns'] = null;
	_filters: TasksTableConfig['filters'] = null;
	private _initialColumns: TasksTableConfig['columns'] = [];

	constructor(preset: PresetView) {
		super(preset);

		makeObservable(this, {
			_groupMode: observable,
			_sortOptions: observable,
			_columns: observable,
			_filters: observable,
			groupMode: computed,
			sortOptions: computed,
			columns: computed,
			filters: computed,
			updateGroupMode: action,
			updateSortOptions: action,
			updateColumns: action,
			updateFilters: action
		});

		this.init();
	}

	init() {
		this.assignServerToClient();
	}

	assignServerToClient = () => {
		const { grouping, sorting, filters, columns } = this.serverPresetView.view_config;
		const sortOptions = sorting?.length
			? {
					id: sorting[0].id,
					asc: !sorting[0].desc
			  }
			: undefined;
		const groupMode = grouping?.length ? grouping[0] : GroupMode.Team;

		const columnsWithFallback = columns?.length ? columns : FALLBACK_COLUMNS;

		this._groupMode = groupMode;
		this._sortOptions = sortOptions;
		// angular tables are use `title` property to show(translate) colum title
		this._initialColumns = columnsWithFallback.map(item => ({
			...item,
			title: item.title_translate_key || item.title,
			active: item.active || false
		}));
		this._columns = this._initialColumns;
		this._filters = filters || {}; // first preset of merchant/user is migrated and does has `filters: null`
	};

	parseClientToServer = (): PresetView => {
		return {
			...this.serverPresetView,
			view_config: {
				grouping: this.groupMode ? [this.groupMode] : [],
				sorting: this.sortOptions ? [{ id: this.sortOptions.id, desc: !this.sortOptions.asc }] : [],
				columns: this.columns.map(item => ({
					...item,
					// angular table return columns without title_translate_key since property title is used as column title
					title_translate_key: item.title
				})),
				filters: this._filters
			}
		};
	};

	get groupMode() {
		return toJS(this._groupMode);
	}

	updateGroupMode = (value: typeof this._groupMode | null | undefined) => {
		this._groupMode = value;
		this.setEdited(true);
	};

	get sortOptions() {
		return toJS(this._sortOptions);
	}

	updateSortOptions = (value: typeof this._sortOptions | null | undefined) => {
		this._sortOptions = value;
		this.setEdited(true);
	};

	get columns() {
		return toJS(this._columns);
	}

	get initialPresetColumns() {
		// when preset is enabled currently columns default to default as initial state of columns of preset(before any changes)
		// investigate which is the proper way the `default` button in Angular Tables should work with preset
		return this._initialColumns;
	}

	updateColumns = (value: typeof this.columns) => {
		this._columns = value;
		this.setEdited(true);
	};

	get filters() {
		return toJS(this._filters);
	}

	updateFilters = (value: typeof this._filters | null | undefined, setTouched = true) => {
		this._filters = value;
		if (setTouched) {
			this.setEdited(true);
		}
	};
}
